import { PAIRING_CODE_ALPHABET, PAIRING_CODE_LENGTH } from './constants.ts';

/**
 * Generate a pairing code from the unambiguous alphabet (no O/0/I/1).
 *
 * Uses `crypto.getRandomValues` with rejection sampling so every symbol is
 * equiprobable (plain `byte % 32` would bias toward lower symbols). The random
 * source is injectable purely so tests can assert distribution/behaviour; in
 * production it always uses the platform CSPRNG (available in Workers, Node ≥18,
 * and browsers).
 */
export function generatePairingCode(
  length: number = PAIRING_CODE_LENGTH,
  alphabet: string = PAIRING_CODE_ALPHABET,
  fillRandom: (buf: Uint8Array<ArrayBuffer>) => void = (buf) => crypto.getRandomValues(buf),
): string {
  const n = alphabet.length;
  if (n < 2 || n > 256) {
    throw new Error(`Pairing alphabet must have 2..256 symbols, got ${n}`);
  }
  // Largest multiple of n that fits in a byte; bytes at/above are rejected.
  const ceiling = Math.floor(256 / n) * n;
  let out = '';
  const buf = new Uint8Array(1);
  while (out.length < length) {
    fillRandom(buf);
    const b = buf[0]!;
    if (b < ceiling) out += alphabet[b % n]!;
  }
  return out;
}

/** True if `code` is a syntactically valid pairing code (length + alphabet). */
export function isValidPairingCode(
  code: string,
  length: number = PAIRING_CODE_LENGTH,
  alphabet: string = PAIRING_CODE_ALPHABET,
): boolean {
  if (code.length !== length) return false;
  for (const ch of code) {
    if (!alphabet.includes(ch)) return false;
  }
  return true;
}
